/**
 * @fileoverview Bulk Action Toolbar Component for Multi-Select Operations
 * @author MCHD Room Scheduler Team
 * @created 08-10-2025 16:37:00
 * 
 * Floating action bar that appears when multiple cells are selected in the planner grid.
 * Provides bulk assignment and selection management functionality.
 */

import React from 'react';
import { Paper, Group, Badge, Button, Text } from '@mantine/core';
import { IconUsers, IconX, IconCheck } from '@tabler/icons-react';

/**
 * Props for BulkActionToolbar component
 */
interface BulkActionToolbarProps {
  /** Number of cells currently selected */
  selectedCount: number;
  
  /** Callback when "Assign Doctor" button clicked */
  onAssign: () => void;
  
  /** Callback when "Clear Selection" button clicked */
  onClear: () => void;
  
  /** Optional loading state for assign action */
  isAssigning?: boolean;
}

/**
 * Bulk Action Toolbar Component
 * 
 * Floating toolbar that appears at bottom center of screen when cells are selected.
 * Provides clear feedback on selection count and actionable buttons for bulk operations.
 * 
 * Implements hospital-grade UX:
 * - Always visible when cells selected (doesn't block grid content)
 * - Clear selection count badge
 * - Prominent "Assign Doctor" CTA
 * - Quick "Clear Selection" option
 * - Smooth entrance/exit animation
 * 
 * @param props - Component props
 * @returns React component (null if no cells selected)
 * 
 * @example
 * ```tsx
 * <BulkActionToolbar
 *   selectedCount={selectedCells.length}
 *   onAssign={() => setShowBulkAssignModal(true)}
 *   onClear={() => clearSelectedCells()}
 * />
 * ```
 * 
 * @created 08-10-2025 16:37:00
 */
export const BulkActionToolbar: React.FC<BulkActionToolbarProps> = ({
  selectedCount,
  onAssign,
  onClear,
  isAssigning = false
}) => {
  // Don't render if no cells selected
  if (selectedCount === 0) return null;
  
  return (
    <Paper
      p="md"
      radius="md"
      withBorder
      shadow="xl"
      style={{
        position: 'fixed',
        bottom: '24px',
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 100,
        background: 'rgba(255, 255, 255, 0.98)',
        backdropFilter: 'blur(10px)',
        border: '2px solid #1e40af',
        boxShadow: '0 8px 32px rgba(30, 64, 175, 0.3)',
        animation: 'slideUp 0.3s ease-out',
        minWidth: '400px'
      }}
      role="toolbar"
      aria-label="Bulk selection actions"
    >
      <Group justify="space-between" align="center" gap="md">
        {/* Selection Count Badge */}
        <Badge 
          size="lg" 
          variant="filled"
          color="blue"
          leftSection={<IconCheck size={14} />}
          style={{ 
            fontWeight: 600,
            fontSize: '14px',
            padding: '8px 16px'
          }}
        >
          {selectedCount} cell{selectedCount !== 1 ? 's' : ''} selected
        </Badge>
        
        {/* Action Buttons */}
        <Group gap="sm">
          <Button
            leftSection={<IconUsers size={16} />}
            onClick={onAssign}
            size="sm"
            variant="filled"
            color="blue"
            loading={isAssigning}
            disabled={isAssigning}
            style={{
              background: '#1e40af',
              fontWeight: 600
            }}
            aria-label={`Assign doctor to ${selectedCount} selected cells`}
          >
            Assign Doctor to Selected
          </Button>
          
          <Button
            leftSection={<IconX size={16} />}
            onClick={onClear}
            size="sm"
            variant="light"
            color="gray"
            disabled={isAssigning}
            style={{
              fontWeight: 500
            }}
            aria-label="Clear cell selection"
          >
            Clear Selection
          </Button>
        </Group>
      </Group>
      
      {/* Optional hint text */}
      <Text 
        size="xs" 
        c="dimmed" 
        mt="xs" 
        ta="center"
        style={{ fontSize: '11px' }}
      >
        Tip: Press ESC to clear selection
      </Text>
      
      {/* CSS Animation */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateX(-50%) translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateX(-50%) translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </Paper>
  );
};

export default BulkActionToolbar;

